import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  DollarSign,
  Calendar,
  User,
  Mail,
  Phone,
  Building2,
  Linkedin,
  Edit,
  TrendingUp,
  AlertCircle,
} from "lucide-react";
import { DealDialog } from "../DealDialog";
import { supabase } from "@/integrations/supabase/client";

interface DealOverviewProps {
  deal: any;
  onUpdate: () => void;
  onSuccess: () => void;
}

export function DealOverview({ deal, onUpdate, onSuccess }: DealOverviewProps) {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [stages, setStages] = useState<any[]>([]);

  useEffect(() => {
    fetchStages();
  }, []);

  const fetchStages = async () => {
    try {
      const { data, error } = await supabase
        .from("pipeline_stages")
        .select("*")
        .order("order_number");

      if (error) throw error;
      setStages(data || []);
    } catch (error) {
      console.error("Error fetching stages:", error);
    }
  };

  const getDealHealth = (probability: number) => {
    if (probability >= 70) return { label: "Healthy", color: "text-green-500", icon: TrendingUp };
    if (probability >= 40) return { label: "At Risk", color: "text-yellow-500", icon: AlertCircle };
    return { label: "Critical", color: "text-red-500", icon: AlertCircle };
  };

  const health = getDealHealth(deal.probability || 50);
  const HealthIcon = health.icon;

  return (
    <div className="space-y-6">
      {/* Deal Health & Quick Stats */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">Deal Health</h3>
            <div className="flex items-center gap-2">
              <HealthIcon className={`h-5 w-5 ${health.color}`} />
              <span className={`font-medium ${health.color}`}>{health.label}</span>
            </div>
          </div>
          <Button onClick={() => setIsEditDialogOpen(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Deal
          </Button>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Win Probability</span>
            <span className="font-medium">{deal.probability || 50}%</span>
          </div>
          <Progress value={deal.probability || 50} className="h-2" />
        </div>
      </Card>

      {/* Deal Details */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Deal Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <DollarSign className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm text-muted-foreground">Value</p>
              <p className="font-semibold">
                {deal.value ? `$${deal.value.toLocaleString()}` : "Not set"}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm text-muted-foreground">Expected Close</p>
              <p className="font-semibold">
                {deal.expected_close_date
                  ? new Date(deal.expected_close_date).toLocaleDateString()
                  : "Not set"}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="h-5 w-5 rounded-full mt-0.5" style={{ backgroundColor: deal.pipeline_stage?.color || "#999" }} />
            <div>
              <p className="text-sm text-muted-foreground">Current Stage</p>
              <p className="font-semibold">{deal.pipeline_stage?.name || "Unknown"}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm text-muted-foreground">Created</p>
              <p className="font-semibold">
                {new Date(deal.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {deal.notes && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm text-muted-foreground mb-2">Notes</p>
            <p className="text-sm whitespace-pre-wrap">{deal.notes}</p>
          </div>
        )}
      </Card>

      {/* Contact Information */}
      {deal.contact && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Contact Information</h3>
            <Badge variant="secondary">Primary Contact</Badge>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">{deal.contact.name}</span>
            </div>

            {deal.contact.email && (
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-muted-foreground" />
                <a
                  href={`mailto:${deal.contact.email}`}
                  className="text-primary hover:underline"
                >
                  {deal.contact.email}
                </a>
              </div>
            )}

            {deal.contact.phone && (
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-muted-foreground" />
                <a
                  href={`tel:${deal.contact.phone}`}
                  className="text-primary hover:underline"
                >
                  {deal.contact.phone}
                </a>
              </div>
            )}

            {deal.contact.organization && (
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <span>{deal.contact.organization}</span>
              </div>
            )}

            {deal.contact.linkedin_profile && (
              <div className="flex items-center gap-3">
                <Linkedin className="h-5 w-5 text-muted-foreground" />
                <a
                  href={deal.contact.linkedin_profile}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  View LinkedIn Profile
                </a>
              </div>
            )}
          </div>
        </Card>
      )}

      <DealDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        deal={deal}
        stages={stages}
        onSuccess={() => {
          onUpdate();
          onSuccess();
        }}
      />
    </div>
  );
}
